import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { sortBy } from 'lodash-es';
import { Alert, Button, ListGroup } from 'react-bootstrap';
import { useApi2 } from 'containers/Api2/index.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import Spinner from 'components/Spinner/index.jsx';
import Switch from 'components/interaction/Switch/index.jsx';
import SelectUsers from 'components/interaction/SelectUsers/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import sameRole from 'utils/sameRole.js';
import { idValues } from 'utils/arrays.js';
import { intlBreak } from 'utils/intlFunctions.jsx';
import { getIdNumber } from 'utils/userIdRole.js';
import useSchoolSettings from '../hooks/useSchoolSettings.js';
import messages from '../messages.js';

function KeyUserType(props) {
  const { id, label, intro, settingsId, userRoles } = props;
  const [showAdd, setShowAdd] = useState(false);
  const role = useCurrentRole();
  const intl = useIntl();
  const { getFetchOptions, fetchSettings } = useSchoolSettings();
  const users = useApi2(getFetchOptions({ url: id }));
  function isDisabled(user) {
    return id === 'managerroles' && sameRole(role, user);
  }
  const settings = useApi2(getFetchOptions({ url: settingsId }));
  const fetchOptions = {
    url: 'sa/users',
    version: 2,
    typeField: 'type',
  };
  const userList = sortBy(idValues(users), 'accountName');
  const exclusions = userList.map(getIdNumber);

  function handleChange(val) {
    return fetchSettings({
      method: 'PUT',
      url: `${settingsId}?employeesEnabled=${val}`,
      body: { employeesEnabled: val },
      replace: false,
    });
  }
  function handleDelete(user) {
    const { id: memberId, memberType, roleType } = user;
    return fetchSettings({
      method: 'DELETE',
      url: `${id}/${memberId}?memberType=${memberType}&roleType=${roleType}`,
    });
  }
  function handleAdd(data) {
    data
      .filter((u) => !exclusions.includes(getIdNumber(u)))
      .forEach((u) => {
        const { id: memberId, roleType } = u;
        fetchSettings({
          method: 'POST',
          url: id,
          body: { memberId, roleType },
        });
      });
  }
  const header = intl.formatMessage(label);
  return (
    <div className="mb-5">
      <h3>{header}</h3>
      <p className="form-text"><FormattedMessage {...intro} values={intlBreak} /></p>
      {users.loading && <Spinner />}
      {users.list && (
        <>
          {'employeesEnabled' in settings && (
            <>
              <div className="d-flex mb-3">
                <Switch
                  value={settings.employeesEnabled}
                  onChange={handleChange}
                />
                <span className="ms-3">
                  <FormattedMessage {...messages.allEmployees} />
                </span>
              </div>
              <p className="form-text"><FormattedMessage {...messages.specificUsers} /></p>
            </>
          )}
          {users.list.length === 0
            ? <Alert variant="info"><FormattedMessage {...messages.noUsers} /></Alert>
            : (
              <ListGroup className="mb-3">
                {userList.map((user) => (
                  <ListGroup.Item key={[user.id, user.roleType]} className="px-0 d-flex justify-content-between align-items-baseline">
                    <UserAvatar user={user} />
                    <DeleteButton
                      disabled={isDisabled(user)}
                      onClick={() => handleDelete(user)}
                      className="py-0 pe-0"
                      confirmText={{ ...messages.confirmRemoveUser, values: user }}
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          <Button variant="link" className="ps-0 fw-semibold" onClick={() => setShowAdd(true)}>
            <FormattedMessage {...messages.addKeyUsers} values={{ type: header }} />
          </Button>
          <SelectUsers
            show={showAdd}
            fetchOptions={fetchOptions}
            value={userList}
            exclusions={exclusions}
            onChange={handleAdd}
            onHide={() => setShowAdd(false)}
            roles={userRoles}
            disableRemove
          />
        </>
      )}
    </div>
  );
}

KeyUserType.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.object.isRequired,
  intro: PropTypes.object.isRequired,
  userRoles: PropTypes.array.isRequired,
  settingsId: PropTypes.string,
};

export default KeyUserType;
